import {RenewableEnergyService} from "./renewableEnergyService";
import {RenewableNumberRequest, type RenewableNumberResponse} from "./renewableNumberRequest";
import {AnimatedNumber, AnimatedNumberFactory} from "../../page/elements/animatedNumber";
import {isNumber} from "../../bootstrap/common/numbers";
import {resolve} from "../../container";
import {Interval} from "../../common/interval";
import {EopImageSpinner} from "../../page/elements/spinner";
import {schedule} from "../../common/utils/promises";
import type {Maybe} from "../../bootstrap/common/types";
import {html, LitElement, type PropertyValues, type TemplateResult} from "lit";
import {customElement, property, query, state} from "lit/decorators.js";
import Styles from "./renewableNumber.lit.scss";

export const RENEWABLE_NUMBER_REFRESH_INTERVAL = 20000;

@customElement("eop-renewable-number")
export class EopRenewableNumber extends LitElement {

    public static readonly styles = Styles;

    private refreshPromise: Promise<void>;
    private animatedNumber: Maybe<AnimatedNumber>;

    @query("eop-image-spinner")
    protected fetchSpinner: EopImageSpinner;

    @property({attribute: "technology"})
    private technology: string = "";
    @property({attribute: "unit"})
    private unit: string = "";
    @property({attribute: "precision", type: Number})
    private precision: number = 1;

    @state()
    private value: string;

    public constructor(
        private renewableEnergyService: RenewableEnergyService = resolve(RenewableEnergyService),
        private interval: Interval = resolve(Interval),
        private animatedNumberFactory: AnimatedNumberFactory = resolve(AnimatedNumberFactory)
    ) {
        super();
    }

    public render(): TemplateResult {
        return html`<span class="animated-number">${this.value}</span></span>
        <eop-image-spinner></eop-image-spinner>`;
    }

    protected firstUpdated(_changedProperties: PropertyValues): void {
        super.firstUpdated(_changedProperties);
        const request = new RenewableNumberRequest(
            this.technology,
            this.unit,
            this.precision);

        void this.fetchSpinner.spinWhile(this.updateNumber(request));
        this.refreshPromise = schedule(this.interval.repeat(
            () => this.updateNumber(request),
            RENEWABLE_NUMBER_REFRESH_INTERVAL))
            .as("fetch");
    }

    private updateNumber(request: RenewableNumberRequest): Promise<void> {
        return schedule(this.fetch(request))
            .as("update-number");
    }

    private async fetch(request: RenewableNumberRequest): Promise<void> {
        try {
            const response = await this.renewableEnergyService.getPowerForTechnology(request);
            this.getAnimatedNumber().update(this.numberFromResponse(response));
        } catch {
            this.getAnimatedNumber().update(Number.NaN);
        }
    }

    private numberFromResponse(response: RenewableNumberResponse): number {
        return isNumber(response.value) ? response.value : Number.NaN;
    }

    private getAnimatedNumber(): AnimatedNumber {
        this.animatedNumber ??= this.animatedNumberFactory.builder()
            .withFixedPrecision(this.precision)
            .withNumberGroupSeparator(true)
            .withInvalidationText("N/A")
            .withUpdateCallback(updatedValue => this.value = updatedValue)
            .appliedTo(this);
        return this.animatedNumber;
    }


    public disconnectedCallback(): void {
        super.disconnectedCallback();
        this.interval.cancel(this.refreshPromise);
    }
}